import React, { useEffect, useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { fetchBrandOpsContracts } from '../../../redux/brandOps/actions';
import { brandOpsContractsSelector, brandOpsFetchingContractsSelector } from '../../../redux/brandOps/selectors';
import { BRAND_SURFACE_CONTAINER_HIGH, BRAND_SURFACE_CONTAINER_HIGHEST } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    MANAGE_PAGE_SX,
    fmtCurrency,
    useBrandManageGate,
} from './manageShared';

// ── Palette ───────────────────────────────────────────────────────────────────
const C_AMBER = '#ffc965';
const C_TEAL = '#00fdc1';
const C_RED = '#ff7351';
const C_MUTED = '#a0a0a0';
const C_BORDER = 'rgba(255,255,255,0.07)';

type StatusConfig = {
    label: string;
    color: string;
    bg: string;
    dot: boolean;
};

function statusConfig(status: string, internal?: string | null): StatusConfig {
    if (internal === 'payment_grace') return { label: 'Payment grace', color: C_AMBER, bg: `${C_AMBER}18`, dot: true };
    if (internal === 'paused') return { label: 'Paused', color: C_RED, bg: `${C_RED}18`, dot: true };
    if (status === 'active' || internal === 'active')
        return { label: 'Active', color: C_TEAL, bg: `${C_TEAL}12`, dot: true };
    if (status === 'awaiting_brand_approval')
        return { label: 'Awaiting approval', color: C_AMBER, bg: `${C_AMBER}14`, dot: true };
    if (status === 'pending_signature')
        return { label: 'Pending signature', color: C_AMBER, bg: `${C_AMBER}14`, dot: false };
    if (status === 'draft') return { label: 'Draft', color: C_MUTED, bg: 'rgba(255,255,255,0.06)', dot: false };
    if (status === 'void' || status === 'voided' || internal === 'voided')
        return { label: 'Voided', color: C_RED, bg: `${C_RED}12`, dot: false };
    if (status === 'ended' || internal === 'ended')
        return { label: 'Ended', color: C_MUTED, bg: 'rgba(255,255,255,0.06)', dot: false };
    return { label: (internal || status).replace(/_/g, ' '), color: C_MUTED, bg: 'rgba(255,255,255,0.06)', dot: false };
}

const cadenceLabel: Record<string, string> = {
    weekly: '/ week',
    monthly: '/ month',
    per_deliverable: '/ deliverable',
};

function ContractCard({ c }: { c: any }) {
    const cfg = statusConfig(c.status, c.internal_status);
    const isAlert = c.internal_status === 'payment_grace' || c.internal_status === 'paused';

    return (
        <Box
            component={RouterLink}
            to={ROUTES.BRAND_MANAGE_CONTRACT_DETAIL.replace(':contractId', c._id)}
            sx={{
                textDecoration: 'none',
                display: 'block',
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: `1px solid ${isAlert ? `${C_AMBER}33` : C_BORDER}`,
                transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s',
                '&:hover': {
                    borderColor: `${C_AMBER}55`,
                    boxShadow: `0 8px 28px rgba(0,0,0,0.45), 0 0 0 1px ${C_AMBER}1a`,
                    transform: 'translateY(-2px)',
                    '& .chevron': { opacity: 1, transform: 'translateX(2px)' },
                },
            }}>
            {/* Top accent */}
            <Box
                sx={{
                    height: 2,
                    background: `linear-gradient(90deg, ${cfg.color} 0%, transparent 100%)`,
                    opacity: 0.7,
                }}
            />

            <Box sx={{ p: 2.25, display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Icon badge */}
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: `${cfg.color}14`,
                        border: `1px solid ${cfg.color}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <GavelIcon sx={{ fontSize: 18, color: cfg.color }} />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: '#fff',
                            fontSize: 13.5,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {c.title || c.manager_email || 'Manager contract'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.4, flexWrap: 'wrap' }}>
                        {c.manager_email && (
                            <Typography sx={{ color: C_MUTED, fontSize: 11.5 }}>{c.manager_email}</Typography>
                        )}
                        {c.fee != null && (
                            <>
                                {c.manager_email && (
                                    <Box
                                        sx={{
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(255,255,255,0.18)',
                                        }}
                                    />
                                )}
                                <Typography sx={{ color: C_AMBER, fontSize: 11.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    {fmtCurrency(c.fee, c.currency)}{' '}
                                    <Box component="span" sx={{ color: C_MUTED }}>
                                        {cadenceLabel[c.cadence] || c.cadence || ''}
                                    </Box>
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Status chip */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        px: 1.25,
                        py: 0.45,
                        borderRadius: 5,
                        bgcolor: cfg.bg,
                        border: `1px solid ${cfg.color}2a`,
                        flexShrink: 0,
                    }}>
                    {cfg.dot && (
                        <Box
                            sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                bgcolor: cfg.color,
                                boxShadow: `0 0 5px ${cfg.color}`,
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            color: cfg.color,
                            fontSize: 10.5,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            textTransform: 'capitalize',
                        }}>
                        {cfg.label}
                    </Typography>
                </Box>

                <ChevronRightIcon
                    className="chevron"
                    sx={{
                        fontSize: 18,
                        color: C_MUTED,
                        opacity: 0,
                        transition: 'opacity 0.15s, transform 0.15s',
                        flexShrink: 0,
                    }}
                />
            </Box>
        </Box>
    );
}

function CardSkeleton() {
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: `1px solid ${C_BORDER}`,
            }}>
            <Box sx={{ height: 2, bgcolor: 'rgba(255,255,255,0.04)' }} />
            <Box sx={{ p: 2.25, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton
                    variant="rounded"
                    width={40}
                    height={40}
                    sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, borderRadius: 1.5 }}
                />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, width: '55%' }} />
                    <Skeleton
                        variant="text"
                        sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, width: '38%', fontSize: 11 }}
                    />
                </Box>
                <Skeleton
                    variant="rounded"
                    width={90}
                    height={24}
                    sx={{ bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, borderRadius: 5 }}
                />
            </Box>
        </Box>
    );
}

export default function BrandManageContractsPage() {
    const dispatch = useDispatch();
    const { brandId } = useBrandManageGate();
    const contracts = useSelector(brandOpsContractsSelector);
    const fetching = useSelector(brandOpsFetchingContractsSelector);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsContracts({ brandId, force: true }));
    }, [dispatch, brandId]);

    const graceContract = useMemo(
        () => contracts.find(c => c.internal_status === 'payment_grace' || c.internal_status === 'paused'),
        [contracts],
    );

    const active = contracts.filter(c => c.internal_status === 'active' || c.status === 'active');
    const pending = contracts.filter(
        c =>
            ['draft', 'pending_signature', 'awaiting_brand_approval'].includes(c.status) &&
            c.internal_status !== 'active',
    );
    const closed = contracts.filter(c => ['void', 'voided', 'ended'].includes(c.internal_status || c.status));

    return (
        <BrandManageGate>
            <Box sx={{ ...MANAGE_PAGE_SX, position: 'relative' }}>
                {/* Ambient glow */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -60,
                        right: -40,
                        width: 280,
                        height: 280,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${C_AMBER}08 0%, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <ManagePageHeader
                        title="Contracts"
                        subtitle="Manager MSAs — create, dispatch, and track signatures."
                        back={ROUTES.BRAND_MANAGE}
                        action={
                            <ManageGoldButton
                                component={RouterLink as any}
                                to={ROUTES.BRAND_MANAGE_CONTRACT_NEW}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AddIcon sx={{ fontSize: 16 }} />
                                New contract
                            </ManageGoldButton>
                        }
                    />

                    {/* Grace alert banner */}
                    {graceContract && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                borderRadius: 2.5,
                                bgcolor: `${C_AMBER}0d`,
                                border: `1px solid ${C_AMBER}33`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: C_AMBER }} />
                                <Box>
                                    <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        {graceContract.internal_status === 'paused'
                                            ? 'Contract paused'
                                            : 'Payment grace'}
                                    </Typography>
                                    <Typography sx={{ color: C_MUTED, fontSize: 12, mt: 0.2 }}>
                                        Escrow could not lock from your wallet.
                                        {graceContract.payment_grace_until
                                            ? ` Grace ends ${new Date(
                                                  graceContract.payment_grace_until,
                                              ).toLocaleString()}.`
                                            : ''}{' '}
                                        Top up to restore manager write access.
                                    </Typography>
                                </Box>
                            </Box>
                            <ManageGoldButton
                                component={RouterLink as any}
                                to={`${
                                    ROUTES.BRAND_WALLET
                                }?topup=1&reason=manager_escrow&contractId=${encodeURIComponent(graceContract._id)}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 15 }} />
                                Top up wallet
                            </ManageGoldButton>
                        </Box>
                    )}

                    {fetching && !contracts.length ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[1, 2, 3].map(i => (
                                <CardSkeleton key={i} />
                            ))}
                        </Box>
                    ) : !contracts.length ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 10,
                                borderRadius: 3,
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                border: `1px solid ${C_BORDER}`,
                            }}>
                            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: -24,
                                        borderRadius: '50%',
                                        background: `radial-gradient(circle, ${C_AMBER}12 0%, transparent 70%)`,
                                        pointerEvents: 'none',
                                    }}
                                />
                                <GavelIcon sx={{ fontSize: 44, color: C_MUTED, opacity: 0.3, position: 'relative' }} />
                            </Box>
                            <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, mb: 0.75 }}>
                                No contracts yet
                            </Typography>
                            <Typography
                                sx={{
                                    color: C_MUTED,
                                    fontSize: 13,
                                    maxWidth: 300,
                                    mx: 'auto',
                                    lineHeight: 1.55,
                                    mb: 3,
                                }}>
                                Create a contract to onboard a content ops manager with escrow-backed compensation.
                            </Typography>
                            <ManageGoldButton component={RouterLink as any} to={ROUTES.BRAND_MANAGE_CONTRACT_NEW}>
                                New contract
                            </ManageGoldButton>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {active.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                bgcolor: C_TEAL,
                                                boxShadow: `0 0 6px ${C_TEAL}`,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: C_TEAL,
                                                fontSize: 10.5,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.09em',
                                            }}>
                                            Active
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                        {active.map(c => (
                                            <ContractCard key={c._id} c={c} />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {pending.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: C_AMBER }} />
                                        <Typography
                                            sx={{
                                                color: C_MUTED,
                                                fontSize: 10.5,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.09em',
                                            }}>
                                            In progress
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                        {pending.map(c => (
                                            <ContractCard key={c._id} c={c} />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {closed.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: C_MUTED,
                                                fontSize: 10.5,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.09em',
                                            }}>
                                            Closed
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                        {closed.map(c => (
                                            <ContractCard key={c._id} c={c} />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </BrandManageGate>
    );
}
